import { Component, OnDestroy, OnInit } from "@angular/core";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { ActivatedRoute, Router } from "@angular/router";
import { MatDialog } from "@angular/material/dialog";
import { MatTableDataSource } from "@angular/material/table";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { Subject } from "rxjs";
import { take, takeUntil } from "rxjs/operators";
import { ApiSettingsService } from "./api-settings.service";
import type { ApiToken } from "../settings/settings.interface";
import { ToastrService } from "../shared/toastr.service";
import { CreateTokenDialogComponent } from "./create-token/create-token-dialog.component";
import { ConfirmationDialogComponent } from "../shared/confirmation-dialog/confirmation-dialog.component";
import { AuthService } from "../auth/auth.service";
import { getBillingSettingsSelector } from "../settings/settings.reducer";
import { isFeatureBlocked } from "../shared/utils/plan-restriction.util";
import { LimitPlanDialogComponent } from "../shared/limit-plan-dialog/limit-plan-dialog.component";
import { environment } from "../../environments/environment";
import type {
  RestApiDoc,
  RestApiDocEndpoint,
  RestApiDocParam,
  RestApiDocSection,
} from "./rest-api-doc.interface";
import restApiDocJson from "../../assets/json/rest-api-doc.json";

const REST_API_DOC = restApiDocJson as unknown as RestApiDoc;
const REST_API_BASE_PATH = "/settings/api";

const STATUS_LABEL_KEYS: Record<string, string> = {
  "200": "restApi.success",
  "201": "restApi.created",
  "204": "restApi.success",
  "400": "restApi.badRequest",
  "401": "restApi.unauthorized",
  "403": "restApi.forbidden",
  "404": "restApi.notFound",
  "429": "restApi.tooManyRequests",
};

const SUCCESS_TAB_ORDER = ["200", "201", "204"];
const ERROR_TAB_ORDER = ["400", "401", "403", "404", "429"];

@Component({
  selector: "app-api-settings",
  templateUrl: "./api-settings.component.html",
  styleUrls: ["./api-settings.component.scss"],
  standalone: false,
})
export class ApiSettingsComponent implements OnInit, OnDestroy {
  tokens: ApiToken[] = [];
  displayedColumns: string[] = [
    "name",
    "keyPrefix",
    "lastUsedAt",
    "expiresAt",
    "createdAt",
    "status",
    "actions",
  ];
  dataSource = new MatTableDataSource<ApiToken>();
  isLoading = true;
  baseUrlCopied = false;
  readonly doc = REST_API_DOC;
  selectedSectionId: string | null = null;
  selectedEndpointId: string | null = null;
  openSectionIds = new Set<string>();
  selectedResponseCode = "200";
  readonly baseUrl = `${environment.restApiUrl.replace(/\/$/, "")}/api/v1`;
  readonly frontendBaseUrl =
    typeof window !== "undefined" ? window.location.origin : "";
  private readonly destroy$ = new Subject<void>();

  constructor(
    private apiSettingsService: ApiSettingsService,
    private dialog: MatDialog,
    private toastr: ToastrService,
    private translate: TranslateService,
    private auth: AuthService,
    private store: Store,
    private sanitizer: DomSanitizer,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  get sections(): RestApiDocSection[] {
    return REST_API_DOC.sections.filter((section) =>
      this.isSectionAvailable(section.id),
    );
  }

  private isSectionAvailable(sectionId: string): boolean {
    const oi = this.auth.appUser?.optionalInterfaces;
    const digitalCardEnabled = !!oi?.digitalCard || this.auth.isSuperAdmin;
    const virtualBackgroundsEnabled =
      !!oi?.virtualBackgrounds || this.auth.isSuperAdmin;
    const emailSignaturesEnabled =
      !!oi?.emailSignatures || this.auth.isSuperAdmin;

    switch (sectionId) {
      case "digital-cards":
        return digitalCardEnabled;
      case "footers":
        return this.auth.hasCompanyManagerAccess && emailSignaturesEnabled;
      case "backgrounds":
        return this.auth.hasCompanyManagerAccess && virtualBackgroundsEnabled;
      case "settings":
      case "company":
      case "employees":
      case "branches":
        return this.auth.hasCompanyManagerAccess;
      default:
        return false;
    }
  }

  ngOnInit(): void {
    this.loadTokens();
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const sectionId = params.get("sectionId");
      const endpointId = params.get("endpointId");
      this.applyRouteSelection(sectionId, endpointId);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private applyRouteSelection(
    sectionId: string | null,
    endpointId: string | null,
  ): void {
    if (!sectionId || !endpointId) {
      this.selectedSectionId = null;
      this.selectedEndpointId = null;
      const firstSection = this.sections[0];
      if (firstSection) {
        this.openSectionIds.add(firstSection.id);
      }
      return;
    }

    const section = this.sections.find((item) => item.id === sectionId);
    const endpoint = section?.endpoints.find((item) => item.id === endpointId);
    if (!section || !endpoint) {
      void this.router.navigate([REST_API_BASE_PATH], { replaceUrl: true });
      return;
    }

    this.selectedSectionId = section.id;
    this.selectedEndpointId = endpoint.id;
    this.openSectionIds.add(section.id);
    this.resetResponseTab();
  }

  get selectedEndpoint(): {
    section: RestApiDocSection;
    endpoint: RestApiDocEndpoint;
  } | null {
    if (!this.selectedSectionId || !this.selectedEndpointId) return null;
    const section = this.sections.find((s) => s.id === this.selectedSectionId);
    const endpoint = section?.endpoints.find(
      (e) => e.id === this.selectedEndpointId,
    );
    return section && endpoint ? { section, endpoint } : null;
  }

  get responseCodes(): string[] {
    const samples = this.selectedEndpoint?.endpoint.responseSamples ?? {};
    const success = SUCCESS_TAB_ORDER.filter((k) =>
      Object.prototype.hasOwnProperty.call(samples, k),
    );
    const errors = ERROR_TAB_ORDER.filter((k) =>
      Object.prototype.hasOwnProperty.call(samples, k),
    );
    return [...success, ...errors];
  }

  loadTokens() {
    this.isLoading = true;
    this.apiSettingsService.getTokens().subscribe({
      next: (tokens) => {
        this.tokens = tokens;
        this.dataSource.data = tokens;
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      },
    });
  }

  openCreateTokenDialog() {
    this.store
      .select(getBillingSettingsSelector)
      .pipe(take(1))
      .subscribe((billingSettings) => {
        if (isFeatureBlocked(this.auth.isSuperAdmin, billingSettings)) {
          const planName = billingSettings?.planName?.toLowerCase() || "basic";
          this.dialog.open(LimitPlanDialogComponent, {
            width: "500px",
            maxWidth: "90vw",
            data: {
              plan: planName as "free" | "freemium" | "basic",
              type: "api",
            },
          });
          return;
        }

        const dialogRef = this.dialog.open(CreateTokenDialogComponent, {
          panelClass: "app-dialog-panel",
          width: "480px",
          maxWidth: "92vw",
        });

        const dialogComponentInstance = dialogRef.componentInstance;
        dialogComponentInstance.tokenCreated$.subscribe(
          (newToken: ApiToken) => {
            const listToken: ApiToken = {
              keyId: newToken.keyId,
              name: newToken.name,
              keyPrefix: newToken.keyPrefix,
              lastUsedAt: newToken.lastUsedAt,
              expiresAt: newToken.expiresAt,
              active: newToken.active,
              createdAt: newToken.createdAt,
              updatedAt: newToken.updatedAt,
              isExpired: newToken.isExpired,
            };
            this.tokens = [listToken, ...this.tokens];
            this.dataSource.data = [...this.tokens];
          },
        );
      });
  }

  deleteToken(tokenId: string) {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: this.translate.instant(
          "settings.api.deleteTokenConfirmation",
        ) as string,
        message: this.translate.instant(
          "settings.api.deleteTokenMessage",
        ) as string,
      },
    });

    dialogRef.afterClosed().subscribe((confirm) => {
      if (confirm) {
        this.apiSettingsService.deleteToken(tokenId).subscribe({
          next: () => {
            this.tokens = this.tokens.filter(
              (token) => token.keyId !== tokenId,
            );
            this.dataSource.data = [...this.tokens];
            this.toastr.success(
              this.translate.instant("settings.api.deleteSuccess") as string,
            );
          },
          error: () => {
            this.toastr.error(
              this.translate.instant("settings.api.deleteError") as string,
            );
          },
        });
      }
    });
  }

  revokeToken(tokenId: string) {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: this.translate.instant(
          "settings.api.revokeTokenConfirmation",
        ) as string,
        message: this.translate.instant(
          "settings.api.revokeTokenMessage",
        ) as string,
      },
    });

    dialogRef.afterClosed().subscribe((confirm) => {
      if (confirm) {
        this.apiSettingsService.revokeToken(tokenId).subscribe({
          next: (updatedToken) => {
            const index = this.tokens.findIndex((t) => t.keyId === tokenId);
            if (index !== -1) {
              this.tokens[index] = updatedToken;
              this.dataSource.data = [...this.tokens];
            }
            this.toastr.success(
              this.translate.instant("settings.api.revokeSuccess") as string,
            );
          },
          error: () => {
            this.toastr.error(
              this.translate.instant("settings.api.revokeError") as string,
            );
          },
        });
      }
    });
  }

  tokenStatusLabel(token: ApiToken): string {
    if (!token.active) return "settings.api.revoked";
    if (token.isExpired) return "settings.api.expired";
    return "settings.api.active";
  }

  tokenStatusClass(token: ApiToken): string {
    if (!token.active) return "status-revoked";
    if (token.isExpired) return "status-expired";
    return "status-active";
  }

  toggleSection(sectionId: string) {
    if (this.openSectionIds.has(sectionId)) {
      this.openSectionIds.delete(sectionId);
    } else {
      this.openSectionIds.add(sectionId);
    }
  }

  isSectionOpen(sectionId: string): boolean {
    return this.openSectionIds.has(sectionId);
  }

  selectEndpoint(sectionId: string, endpointId: string) {
    if (
      this.selectedSectionId === sectionId &&
      this.selectedEndpointId === endpointId
    ) {
      return;
    }
    void this.router.navigate([REST_API_BASE_PATH, sectionId, endpointId], {
      replaceUrl: true,
    });
  }

  copyBaseUrl() {
    void navigator.clipboard.writeText(this.baseUrl);
    this.baseUrlCopied = true;
    this.toastr.success(this.translate.instant("restApi.copied") as string);
    setTimeout(() => {
      this.baseUrlCopied = false;
    }, 2000);
  }

  copyText(text: string) {
    void navigator.clipboard.writeText(text);
    this.toastr.success(this.translate.instant("restApi.copied") as string);
  }

  methodClass(method: string): string {
    switch (method) {
      case "GET":
        return "method-get";
      case "POST":
        return "method-post";
      case "PUT":
        return "method-put";
      case "PATCH":
        return "method-patch";
      case "DELETE":
        return "method-delete";
      default:
        return "method-default";
    }
  }

  statusClass(code: string): string {
    if (code.startsWith("2")) return "status-success";
    if (code === "429" || code === "401") return "status-warn";
    return "status-error";
  }

  statusLabelKey(code: string): string {
    const endpoint = this.selectedEndpoint?.endpoint;
    return (
      endpoint?.responseDescriptions?.[code] ??
      STATUS_LABEL_KEYS[code] ??
      "restApi.success"
    );
  }

  replacePlaceholders(text: string): string {
    let out = text.replace(
      new RegExp(this.doc.baseUrlPlaceholder.replace(/[{}]/g, "\\$&"), "g"),
      this.baseUrl,
    );
    const fePlaceholder =
      this.doc.frontendBaseUrlPlaceholder ?? "{{frontendBaseUrl}}";
    if (fePlaceholder && this.frontendBaseUrl) {
      out = out.replace(
        new RegExp(fePlaceholder.replace(/[{}]/g, "\\$&"), "g"),
        this.frontendBaseUrl,
      );
    }
    return out;
  }

  formatSample(sample: string | null | undefined): string {
    if (sample == null || sample.trim() === "") return "";
    const replaced = this.replacePlaceholders(sample);
    try {
      return JSON.stringify(JSON.parse(replaced) as unknown, null, 2);
    } catch {
      return replaced;
    }
  }

  highlightCode(code: string): SafeHtml {
    const highlighted = code
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(
        /(".*?"(?=\s*:))|(\b[a-z_$][\w$]*\b(?=\s*:))|(".*?"|'.*?')|\b(const|let|var|fetch|then|catch|true|false|null|return)\b|(-?\d+(?:\.\d+)?)/g,
        (match, keyQuotes, keyNoQuotes, str, kw, num) => {
          if (keyQuotes || keyNoQuotes)
            return `<span class="syntax-key">${match}</span>`;
          if (str) return `<span class="syntax-string">${str}</span>`;
          if (kw) return `<span class="syntax-keyword">${kw}</span>`;
          if (num) return `<span class="syntax-number">${num}</span>`;
          return match;
        },
      );
    return this.sanitizer.bypassSecurityTrustHtml(highlighted);
  }

  downloadDocumentationJson() {
    const paths: Record<string, Record<string, unknown>> = {};
    const tags = this.sections.map((s) => ({
      name: this.translate.instant(s.labelKey) as string,
      description: s.id,
    }));

    for (const section of this.sections) {
      for (const ep of section.endpoints) {
        const pathKey = ep.path;
        if (!paths[pathKey]) paths[pathKey] = {};
        const method = ep.method.toLowerCase();
        const parameters: Array<{
          name: string;
          in: string;
          required: boolean;
          description: string;
          schema: { type: string };
        }> = [];
        for (const p of ep.pathParams ?? []) {
          parameters.push({
            name: p.name,
            in: "path",
            required: true,
            description: this.translate.instant(p.descriptionKey) as string,
            schema: { type: this.openApiTypeFromKey(p.typeKey) },
          });
        }
        for (const p of ep.queryParams ?? []) {
          parameters.push({
            name: p.name,
            in: "query",
            required: !!p.required,
            description: this.translate.instant(p.descriptionKey) as string,
            schema: { type: this.openApiTypeFromKey(p.typeKey) },
          });
        }

        const responses: Record<string, unknown> = {};
        for (const [code, sample] of Object.entries(ep.responseSamples)) {
          const resolved =
            sample == null ? null : this.replacePlaceholders(sample);
          if (code === "204" || resolved === null) {
            responses[code] = {
              description: code === "204" ? "No content" : "Empty",
            };
          } else {
            try {
              responses[code] = {
                description: `${code} response`,
                content: {
                  "application/json": {
                    example: JSON.parse(resolved) as unknown,
                  },
                },
              };
            } catch {
              responses[code] = {
                description: `${code} response`,
                content: {
                  "application/json": { schema: { type: "object" } },
                },
              };
            }
          }
        }

        const operation: Record<string, unknown> = {
          operationId: ep.id,
          tags: [section.id],
          summary: this.translate.instant(ep.titleKey) as string,
          description: this.translate.instant(ep.descriptionKey) as string,
          parameters,
          responses,
          security: [{ bearerAuth: [] }],
        };

        if (
          (ep.method === "POST" || ep.method === "PUT") &&
          (ep.bodyParams?.length ?? 0) > 0
        ) {
          const properties: Record<string, unknown> = {};
          const required: string[] = [];
          for (const p of ep.bodyParams ?? []) {
            properties[p.name] = this.buildPropertySchema(p);
            if (p.required) required.push(p.name);
          }
          operation.requestBody = {
            required: required.length > 0,
            content: {
              "application/json": {
                schema: {
                  type: "object",
                  properties,
                  ...(required.length > 0 ? { required } : {}),
                },
              },
            },
          };
        }

        paths[pathKey][method] = operation;
      }
    }

    const openApi = {
      openapi: "3.0.3",
      info: {
        title: "gSignature REST API",
        description: this.translate.instant("restApi.description") as string,
        version: this.doc.version,
      },
      servers: [{ url: this.baseUrl }],
      tags,
      paths,
      components: {
        securitySchemes: {
          bearerAuth: {
            type: "http",
            scheme: "bearer",
            bearerFormat: "Bearer",
            description: "API token (Bearer token)",
          },
        },
      },
      security: [{ bearerAuth: [] }],
    };

    const blob = new Blob([JSON.stringify(openApi, null, 2)], {
      type: "application/json",
    });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `gsignature-openapi-${this.doc.version}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  trackParam(_: number, param: RestApiDocParam): string {
    return param.name;
  }

  private openApiTypeFromKey(key: string): string {
    const normalized = key.toLowerCase();
    if (normalized.includes("number") || normalized.includes("integer"))
      return "number";
    if (normalized.includes("boolean")) return "boolean";
    if (normalized.includes("array")) return "array";
    if (normalized.includes("object")) return "object";
    return "string";
  }

  private buildPropertySchema(param: RestApiDocParam): Record<string, unknown> {
    const schema: Record<string, unknown> = {
      type: this.openApiTypeFromKey(param.typeKey),
      description: this.translate.instant(param.descriptionKey) as string,
    };

    if (param.properties?.length) {
      const nestedProps: Record<string, unknown> = {};
      const nestedRequired: string[] = [];
      for (const prop of param.properties) {
        nestedProps[prop.name] = this.buildPropertySchema(prop);
        if (prop.required) nestedRequired.push(prop.name);
      }
      schema.properties = nestedProps;
      if (nestedRequired.length > 0) {
        schema.required = nestedRequired;
      }
    }

    if (param.oneOf?.length) {
      schema.oneOf = param.oneOf.map((variant) =>
        this.buildPropertySchema(variant),
      );
    }

    return schema;
  }

  private resetResponseTab() {
    const codes = this.responseCodes;
    const endpoint = this.selectedEndpoint?.endpoint;
    if (endpoint?.method === "POST" && codes.includes("201")) {
      this.selectedResponseCode = "201";
    } else if (codes.includes("200")) {
      this.selectedResponseCode = "200";
    } else {
      this.selectedResponseCode = codes[0] ?? "200";
    }
  }
}
